import { useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Globe, Send, Loader2 } from "lucide-react";

const EMAILJS_SERVICE_ID = "service_1cvl99w";
const EMAILJS_TEMPLATE_ID = "template_mtvypid";
const EMAILJS_PUBLIC_KEY = "hG_rJjyxuBKDymmAJ";

export default function Contact() {
    const formRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
    });

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Por favor completa los campos requeridos.");
            return;
        }
        setLoading(true);
        try {
            await emailjs.sendForm(
                EMAILJS_SERVICE_ID,
                EMAILJS_TEMPLATE_ID,
                formRef.current,
                { publicKey: EMAILJS_PUBLIC_KEY },
            );
            toast.success(
                "¡Mensaje enviado! Te contactaremos pronto.",
            );
            setForm({
                name: "",
                email: "",
                phone: "",
                subject: "",
                message: "",
            });
        } catch (err) {
            console.error("EmailJS error:", err);
            toast.error(
                "No se pudo enviar el mensaje. Intenta de nuevo o escríbenos directo a contacto@creacionesopteris.com",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="contacto"
            data-testid="contact-section"
            className="py-20 lg:py-32 bg-white relative overflow-hidden"
        >
            <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#f4861c]/10 blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-[#ea4d87]/10 blur-3xl pointer-events-none" />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
                    {/* Left: Info */}
                    <div className="lg:col-span-5 fade-up">
                        <span className="opteris-chip">Contacto</span>
                        <h2
                            data-testid="contact-heading"
                            className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-5 leading-[1.1]"
                        >
                            Solicita tu{" "}
                            <span className="text-[#ea4d87]">
                                cotización
                            </span>
                        </h2>
                        <p className="mt-5 text-gray-600 leading-relaxed">
                            Cuéntanos qué necesitas y nuestro equipo se pondrá
                            en contacto contigo a la brevedad.
                        </p>

                        <div className="mt-10 space-y-5">
                            <InfoItem
                                icon={<MapPin size={18} />}
                                title="Dirección"
                                content="Esteban Alatorre 1288-A int. 4, San Juan Bosco C.P. 44730, Guadalajara, Jalisco, México."
                                testid="info-address"
                            />
                            <InfoItem
                                icon={<Phone size={18} />}
                                title="Teléfono"
                                content="33 2168 5009"
                                href="tel:3321685009"
                                testid="info-phone"
                            />
                            <InfoItem
                                icon={<Mail size={18} />}
                                title="Email"
                                content="contacto@creacionesopteris.com"
                                href="mailto:contacto@creacionesopteris.com"
                                testid="info-email"
                            />
                            <InfoItem
                                icon={<Globe size={18} />}
                                title="Sitio web"
                                content="creacionesopteris.com"
                                testid="info-website"
                            />
                        </div>
                    </div>

                    {/* Right: Form */}
                    <div className="lg:col-span-7 fade-up">
                        <div className="bg-white border border-gray-100 rounded-[2rem] p-6 sm:p-10 shadow-xl">
                            <form
                                ref={formRef}
                                onSubmit={handleSubmit}
                                data-testid="contact-form"
                                className="space-y-5"
                            >
                                <div className="grid sm:grid-cols-2 gap-5">
                                    <Field
                                        label="Nombre *"
                                        name="name"
                                        value={form.name}
                                        onChange={handleChange}
                                        placeholder="Tu nombre"
                                        testid="input-name"
                                        required
                                    />
                                    <Field
                                        label="Correo electrónico *"
                                        name="email"
                                        type="email"
                                        value={form.email}
                                        onChange={handleChange}
                                        placeholder="correo@empresa.com"
                                        testid="input-email"
                                        required
                                    />
                                </div>
                                <div className="grid sm:grid-cols-2 gap-5">
                                    <Field
                                        label="Teléfono"
                                        name="phone"
                                        value={form.phone}
                                        onChange={handleChange}
                                        placeholder="33 0000 0000"
                                        testid="input-phone"
                                    />
                                    <Field
                                        label="Asunto"
                                        name="subject"
                                        value={form.subject}
                                        onChange={handleChange}
                                        placeholder="Ej: Cotización mayoreo"
                                        testid="input-subject"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                                        Mensaje *
                                    </label>
                                    <textarea
                                        name="message"
                                        value={form.message}
                                        onChange={handleChange}
                                        required
                                        rows={5}
                                        data-testid="input-message"
                                        placeholder="Cuéntanos qué productos o servicios necesitas..."
                                        className="opteris-input w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-900 placeholder-gray-400 transition resize-none"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="contact-submit-button"
                                    className="opteris-btn-primary w-full sm:w-auto justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2
                                                size={18}
                                                className="animate-spin"
                                            />
                                            Enviando...
                                        </>
                                    ) : (
                                        <>
                                            Enviar mensaje
                                            <Send size={16} />
                                        </>
                                    )}
                                </button>

                                <p className="text-xs text-gray-500 pt-2">
                                    Al enviar este formulario aceptas que nos
                                    comuniquemos contigo para responder a tu
                                    solicitud.
                                </p>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function InfoItem({ icon, title, content, href, testid }) {
    const body = (
        <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-[#f4861c]/10 text-[#f4861c] flex items-center justify-center flex-shrink-0">
                {icon}
            </div>
            <div>
                <div className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                    {title}
                </div>
                <div className="text-gray-900 mt-1 font-medium">
                    {content}
                </div>
            </div>
        </div>
    );
    return href ? (
        <a
            href={href}
            data-testid={testid}
            className="block group hover:-translate-y-0.5 transition-transform"
        >
            {body}
        </a>
    ) : (
        <div data-testid={testid}>{body}</div>
    );
}

function Field({ label, name, type = "text", value, onChange, placeholder, required, testid }) {
    return (
        <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                {label}
            </label>
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                required={required}
                placeholder={placeholder}
                data-testid={testid}
                className="opteris-input w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-900 placeholder-gray-400 transition"
            />
        </div>
    );
}
